import React from "react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const validationSchema = Yup.object({
  field_label: Yup.string().required("Field label is required"),
  type: Yup.string().required("Field type is required"),
});

const FIELD_TYPE_OPTIONS = ["PDFTextField", "PDFCheckBox"];

const createExtraFieldName = () => {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `extra_field_${crypto.randomUUID()}`;
  }

  return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const AddFieldDialog = ({
  open,
  handleClose,
  currentProcessingFile,
  setCurrentProcessingFile,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const formik = useFormik({
    initialValues: {
      field_label: "",
      type: null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setCurrentProcessingFile((prevFile) => {
        const maxOrder = [...prevFile.document.tempFields.formFields]?.sort(
          (a, b) => b.order - a.order,
        )?.[0]?.order;
        const generatedName = createExtraFieldName();

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: [
                ...prevFile.document.tempFields.formFields,
                {
                  name: generatedName,
                  field_label: values?.field_label,
                  type: values?.type,
                  value: values?.type === "PDFCheckBox" ? false : "",
                  x: 0, // Convert X to points
                  y: 0, // Convert Y to points and flip
                  width: 0, // Convert width to points
                  height: 0, // Convert height to points
                  page: prevFile?.document?.tempFields?.currentDocument
                    ?.pageNumber,
                  fontSize: 10,
                  fontSizes: [],
                  autoFontSize: true,
                  hidden: true,
                  order: (maxOrder ?? 0) + 1,
                },
              ],
            },
          },
        };
      });
      handleClose();
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    resetForm,
  } = formik;

  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            Create Extra Form Field
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCloseModal}
          disabled={isSubmitting}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <FormikProvider value={formik}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Field Label"
                  name="field_label"
                  value={values.field_label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.field_label && Boolean(errors.field_label)}
                  helperText={touched.field_label && errors.field_label}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <Autocomplete
                  value={values.type}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("type", newValue);
                  }}
                  onBlur={handleBlur}
                  options={FIELD_TYPE_OPTIONS}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Field Type"
                      name="type"
                      error={touched.type && Boolean(errors.type)}
                      helperText={touched.type && errors.type}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              variant="text"
              disabled={isSubmitting}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {isSubmitting ? "Saving…" : "Save Field"}
            </Button>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

export default AddFieldDialog;
